import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import Flex from '../../../../components/Flex';
import { Typography } from 'antd';
import { useTheme } from 'theming';
import { PrioTheme } from '../../../../theme/types';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectNews, getUserMe } from '../../../../apps/main/rootReducer';
import useProjectsContext from '../../../projects/hooks/useProjectsContext';
import { ProjectId, ProjectNewsType } from '../../../../models/Types';
import {
  Button,
  Divider,
  Dropdown,
  Menu,
  PillCRA as Pill,
} from '@prio365/prio365-react-library';
import MenuItem from '@prio365/prio365-react-library/lib/Menu/components/MenuItem';
import {
  ProjectNews,
  ProjectNewsCategoryKey,
} from '../../../../models/Project';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  deleteAllProjectNews,
  deleteProjectNewsByProjectId,
  fetchProjectNewsCounts,
  fetchProjectNewsInSaga,
  setActiveProjectTabView,
} from '../../../projects/actions';
import { IconName } from '@fortawesome/pro-light-svg-icons';
import { assigneeReadMessageProject } from '../../../mail/actions/projects/messages';
import FontAwesomeCustomIcon from '../../../../components/FontAwesomeCustomIcon';
import { useNavigate } from 'react-router-dom';
const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  spinner: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contextMenu: {
    fontSize: theme.font.fontSize.small,
    padding: 0,
    '& .prio-menu': {
      padding: 0,
    },
    '& .prio-menu-item': {
      marginLeft: 0,
    },
  },
  wrapper: {
    overflowY: 'auto',
  },
  row: {
    padding: `${theme.spacing.small}px 0px`,
    '& button': {
      visibility: 'hidden',
    },
    '&:hover button': {
      visibility: 'visible',
    },
  },
  projectName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pillsContainer: {
    overflowX: 'auto',
  },
  divider: { margin: 0 },
  pillContainer: {
    cursor: 'pointer',
  },
}));

type SortOrder = 'default' | 'desc' | 'asc';

interface DashboardProjectNewsItemProps {
  className?: string;
}

export const DashboardProjectNewsItem: React.FC<
  DashboardProjectNewsItemProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const dispatch = useDispatch();

  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const projectNews = useSelector(getProjectNews);
  const { getProjectById } = useProjectsContext();
  const noProjectNews = Object.keys(projectNews).length === 0;

  const [sortOrder, setSortOrder] = React.useState<SortOrder>('default');

  const projectNewsSorted: ProjectNews = useMemo(() => {
    const projectNewsArray = Object.entries(projectNews);
    const numberOfProjectNewsByProjectId = projectNewsArray.map(
      ([projectId, news]) => {
        return {
          projectId,
          numberOfProjectNews: Object.values(news).reduce(
            (acc, newsItems) => acc + newsItems.length,
            0
          ),
        };
      }
    );

    if (sortOrder === 'asc') {
      numberOfProjectNewsByProjectId.sort((a, b) => {
        if (a.numberOfProjectNews === b.numberOfProjectNews) {
          const projectA = getProjectById(a.projectId);
          const projectB = getProjectById(b.projectId);
          return (projectA?.number ?? '').localeCompare(projectB?.number ?? '');
        }
        return a.numberOfProjectNews - b.numberOfProjectNews;
      });
    }
    if (sortOrder === 'desc') {
      numberOfProjectNewsByProjectId.sort((a, b) => {
        if (a.numberOfProjectNews === b.numberOfProjectNews) {
          const projectA = getProjectById(a.projectId);
          const projectB = getProjectById(b.projectId);
          return (projectA?.number ?? '').localeCompare(projectB?.number ?? '');
        }
        return b.numberOfProjectNews - a.numberOfProjectNews;
      });
    }

    if (sortOrder === 'default') {
      numberOfProjectNewsByProjectId.sort((a, b) => {
        const projectA = getProjectById(a.projectId);
        const projectB = getProjectById(b.projectId);
        return (projectA?.number ?? '').localeCompare(projectB?.number ?? '');
      });
    }

    return numberOfProjectNewsByProjectId.reduce((acc, item) => {
      const projectId = item.projectId;
      const news = projectNews[projectId];
      return {
        ...acc,
        [projectId]: news,
      };
    }, {} as ProjectNews);
  }, [projectNews, sortOrder, getProjectById]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onSortClick = () => {
    if (sortOrder === 'default') {
      setSortOrder('desc');
      return;
    }
    if (sortOrder === 'desc') {
      setSortOrder('asc');
      return;
    }
    setSortOrder('default');
  };

  const getSortOrderIcon = (sortOrder: SortOrder): IconName => {
    return sortOrder === 'asc'
      ? 'arrow-up'
      : sortOrder === 'desc'
        ? 'arrow-down'
        : 'arrow-down-arrow-up';
  };

  const getSortOrderTooltip = (sortOrder: SortOrder): string => {
    return sortOrder === 'asc'
      ? t('dashboard:projectNews.sortOrder.asc')
      : sortOrder === 'desc'
        ? t('dashboard:projectNews.sortOrder.desc')
        : t('dashboard:projectNews.sortOrder.default');
  };

  const resetMessageAssignmentUnreadProjectNews = (
    projectId: ProjectId,
    projectNews: ProjectNewsCategoryKey
  ) => {
    const messageAssignmentUnread = projectNews?.messageAssignmentUnread || [];

    if (messageAssignmentUnread.length > 0) {
      messageAssignmentUnread.forEach((projectNews) => {
        const messageId = projectNews.referenceObjectId;
        dispatch(assigneeReadMessageProject(projectId, messageId));
      });
    }
  };

  const resetAllProjectNews = () => {
    dispatch(deleteAllProjectNews());
    Object.entries(projectNews).forEach(([projectId, projectNews]) => {
      resetMessageAssignmentUnreadProjectNews(projectId, projectNews);
    });
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    dispatch(fetchProjectNewsCounts());
    dispatch(fetchProjectNewsInSaga());
  }, [dispatch]);
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Row childrenGap={theme.old.spacing.baseSpacing} marginBottom={8}>
        <Flex.Item flex={1}>
          <Typography.Title level={3} ellipsis style={{ marginBottom: 0 }}>
            {t('dashboard:projectNews.title')}
          </Typography.Title>
        </Flex.Item>
        {!noProjectNews && (
          <>
            <Button
              type="link"
              iconProp={['fal', getSortOrderIcon(sortOrder)]}
              onClick={onSortClick}
              tooltip={getSortOrderTooltip(sortOrder)}
              iconColor={theme.colors.application.typography.muted}
            />
            <Dropdown
              trigger={['click']}
              overlayClassName={classes.contextMenu}
              placement="bottomRight"
              overlay={
                <Menu selectedItemId="">
                  <MenuItem id="1" onClick={resetAllProjectNews}>
                    {t('dashboard:projectNews.actions.deleteAll')}
                  </MenuItem>
                </Menu>
              }
            >
              <Button
                type="link"
                iconProp={['fal', 'ellipsis-v']}
                iconColor={theme.colors.application.typography.muted}
              />
            </Dropdown>
          </>
        )}
      </Flex.Row>
      <InnerComponent
        projectNews={projectNewsSorted}
        resetMessageAssignmentUnreadProjectNews={
          resetMessageAssignmentUnreadProjectNews
        }
      />
    </Flex.Column>
  );
};

export default DashboardProjectNewsItem;

interface InnerComponentProps {
  projectNews: ProjectNews;
  resetMessageAssignmentUnreadProjectNews: (
    projectId: ProjectId,
    news: ProjectNewsCategoryKey
  ) => void;
}

export const InnerComponent: React.FC<InnerComponentProps> = (props) => {
  //#region ------------------------------ Defaults
  const { projectNews, resetMessageAssignmentUnreadProjectNews } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const me = useSelector(getUserMe);
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { getProjectById } = useProjectsContext();

  const noProjectNews = Object.keys(projectNews).length === 0;
  const projects = Object.entries(projectNews);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const resetProjectNews = (
    projectId: ProjectId,
    projectNews: ProjectNewsCategoryKey
  ) => {
    dispatch(deleteProjectNewsByProjectId(projectId));
    resetMessageAssignmentUnreadProjectNews(projectId, projectNews);
  };

  const handleOnPillClick = async (projectId: string) => {
    dispatch(setActiveProjectTabView('myProjects'));
    navigate(`/module/prio/projects/${projectId}/mail/inbox`, {
      state: {
        presetFilter: {
          assignedTo: me.id,
          reassigned: true,
        },
        isAllFolders: true,
      },
    });
  };

  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  //#region ------------------------------ Components
  const renderPill = (type: ProjectNewsType, number: number) => {
    let icon;
    switch (type) {
      case 'messageAssignmentUnread':
        icon = 'light-envelope-user';
        break;
      case 'messageUnread':
        icon = 'envelope';
        break;
      default:
        icon = undefined;
    }

    const renderIcon = () => {
      if (icon && type === 'messageAssignmentUnread') {
        return <FontAwesomeCustomIcon icon={icon} />;
      }
      if (icon) {
        return <FontAwesomeIcon icon={['fal', icon]} />;
      }
      return null;
    };

    return (
      <Pill
        style={{ width: 'fit-content' }}
        tooltip={t(`dashboard:projectNews.types.${type}`)}
      >
        <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
          {renderIcon()}
          <span style={{ color: theme.colors.application.typography.default }}>
            {number}
          </span>
        </Flex.Row>
      </Pill>
    );
  };

  const renderRow = (
    projectId: ProjectId,
    news: ProjectNewsCategoryKey,
    showDivider: boolean
  ) => {
    const project = getProjectById(projectId);
    return (
      <div>
        <Flex.Row
          alignItems="center"
          className={classes.row}
          childrenGap={theme.spacing.small}
        >
          <Flex.Row
            flex={1}
            alignItems="center"
            childrenGap={theme.spacing.small}
            overflow={'hidden'}
          >
            <Flex.Item flex={1}>
              <div
                className={classes.projectName}
                title={
                  project
                    ? `${project.number} - ${project.shortName}`
                    : undefined
                }
              >
                {project ? `${project.number} - ${project.shortName}` : '-'}
              </div>
            </Flex.Item>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayClassName={classes.contextMenu}
              overlay={
                <Menu selectedItemId="">
                  <MenuItem
                    id="1"
                    onClick={() => resetProjectNews(projectId, news)}
                  >
                    {t('dashboard:projectNews.actions.delete')}
                  </MenuItem>
                </Menu>
              }
            >
              <Button type="link" iconProp={['fal', 'ellipsis-v']} />
            </Dropdown>
          </Flex.Row>
          <Flex.Row
            justifyContent="flex-end"
            childrenGap={theme.spacing.small}
            className={classes.pillsContainer}
          >
            {Object.entries(news).map(([newsCategory, newsItems]) => {
              return (
                <div
                  className={classes.pillContainer}
                  onClick={() => handleOnPillClick(projectId)}
                >
                  {renderPill(
                    newsCategory as ProjectNewsType,
                    newsItems.length
                  )}
                </div>
              );
            })}
          </Flex.Row>
        </Flex.Row>
        {showDivider && <Divider className={classes.divider} />}
      </div>
    );
  };
  // #endregion

  if (noProjectNews) {
    return (
      <>
        <Flex.Row justifyContent="center" paddingTop={theme.spacing.large}>
          <div
            style={{
              color: theme.colors.application.typography.muted,
              fontSize: theme.font.fontSize.small,
            }}
          >
            {t('dashboard:projectNews.noProjectNews')}
          </div>
        </Flex.Row>
        <Flex.Row justifyContent="center" paddingTop={theme.spacing.regular}>
          <div
            style={{
              color: theme.colors.application.typography.muted,
              fontSize: theme.font.fontSize.extraSmall,
            }}
          >
            {t('dashboard:projectNews.noProjectNews2')}
          </div>
        </Flex.Row>
        <Flex.Row justifyContent="center" paddingTop={theme.spacing.small}>
          <FontAwesomeIcon
            icon={['fal', 'thumbs-up']}
            color={theme.colors.base.blue[50]}
            size="2xl"
          />
        </Flex.Row>
      </>
    );
  }

  return (
    <Flex.Column className={classes.wrapper}>
      {projects.map(([projectId, news], index) =>
        renderRow(projectId, news, index !== projects.length - 1)
      )}
    </Flex.Column>
  );
};
